import { Loader2 } from "lucide-react";
import { FormProvider, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { FormActionButtons, NavigationGuardProvider } from "@/lib/components/navigation-guard";
import { PageContent } from "@/lib/components/page-content";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import { AutoBookingSettings, BookingModeSelector, ManualBookingSettings } from "./components";
import { useBookingBehaviorSettings } from "./hooks/use-booking-behavior-settings";

export function BookingBehaviorPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation("settings");
  const { form, loading, saving, save, discard, error, saveField, savingFields } =
    useBookingBehaviorSettings();

  // Watch booking mode using useWatch for React 19 + compiler optimization
  const bookingMode = useWatch({ control: form.control, name: "bookingMode" });

  if (loading) {
    return (
      <>
        <BaseNavigationBar
          title={t("sections.bookingBehavior.title")}
          subtitle={t("sections.bookingBehavior.description")}
          leadElement={
            isMobile ? <BackButton key="back-button" onBack={() => navigate(-1)} /> : undefined
          }
        />

        <PageContent>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 flex items-center justify-center">
              <div className="flex items-center space-x-3">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                <span className="text-lg">{t("status.loadingConfiguration")}</span>
              </div>
            </CardContent>
          </Card>
        </PageContent>
      </>
    );
  }

  return (
    <NavigationGuardProvider
      hasUnsavedChanges={form.formState.isDirty}
      onSave={save}
      onDiscard={discard}
      saving={saving}
      description={t("messages.unsavedChanges")}
    >
      <BaseNavigationBar
        title={t("sections.bookingBehavior.title")}
        subtitle={t("sections.bookingBehavior.description")}
        leadElement={
          isMobile ? <BackButton key="back-button" onBack={() => navigate(-1)} /> : undefined
        }
        trailingElements={[
          <SettingsSearchTrigger key="search-trigger" />,
          <FormActionButtons
            key="form-actions"
            isDirty={form.formState.isDirty}
            onSave={save}
            onDiscard={discard}
            saving={saving}
          />,
        ]}
      />

      <PageContent>
        <FormProvider {...form}>
          <div className="space-y-6">
            <ErrorAlert error={error} />

            {!error && (
              <>
                <div id="field-booking-mode">
                  <BookingModeSelector
                    value={bookingMode}
                    onChange={(mode) => void saveField("bookingMode", mode)}
                    disabled={savingFields.has("bookingMode")}
                  />
                </div>

                {bookingMode === "manual" ? (
                  <ManualBookingSettings saveField={saveField} savingFields={savingFields} />
                ) : (
                  <AutoBookingSettings saveField={saveField} savingFields={savingFields} />
                )}
              </>
            )}
          </div>
        </FormProvider>
      </PageContent>
    </NavigationGuardProvider>
  );
}
